import { useNavigate, createFileRoute } from '@tanstack/react-router'
import * as React from "react";
import { API_ADMIN_BASE_URL } from "@/lib/api-client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { cn } from "@/lib/utils";
import { SidebarTrigger } from "@/components/ui/sidebar";
import {
  Search,
  ChevronsUpDown,
  Check,
  X,
  Plus,
  Link as LinkIcon,
  Mail,
  Wallet,
  Copy,
  Ban,
  Timer,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";

// ─── Types ───────────────────────────────────────────────────────────

interface InviteUser {
  id: string;
  name: string;
  email: string;
  image?: string;
}

interface Invite {
  id: string;
  type: string;
  email: string | null;
  walletAddress: string | null;
  code: string;
  linkSlug: string | null;
  status: string;
  role: string;
  message: string | null;
  expiresAt: string;
  usedAt: string | null;
  createdAt: string;
  invitedBy: InviteUser;
  acceptedBy: InviteUser | null;
}

interface InviteStats {
  total: number;
  pending: number;
  accepted: number;
  expired: number;
  revoked: number;
  typeDistribution: Record<string, number>;
}

// ─── Helpers ─────────────────────────────────────────────────────────

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function getStatusColor(status: string): string {
  switch (status) {
    case "PENDING": return "text-amber-600";
    case "ACCEPTED": return "text-green-600";
    case "EXPIRED": return "text-muted-foreground";
    case "REVOKED": return "text-red-600";
    default: return "";
  }
}

function getStatusVariant(status: string): "default" | "secondary" | "outline" | "destructive" {
  switch (status) {
    case "ACCEPTED": return "default";
    case "PENDING": return "secondary";
    case "REVOKED": return "destructive";
    default: return "outline";
  }
}

function getTypeIcon(type: string) {
  switch (type) {
    case "LINK": return <LinkIcon className="h-3.5 w-3.5" />;
    case "EMAIL": return <Mail className="h-3.5 w-3.5" />;
    case "WALLET": return <Wallet className="h-3.5 w-3.5" />;
    default: return null;
  }
}

function getInviteTarget(invite: Invite): string {
  if (invite.type === "LINK" && invite.linkSlug) return invite.linkSlug;
  if (invite.type === "EMAIL" && invite.email) return invite.email;
  if (invite.type === "WALLET" && invite.walletAddress) return truncateAddress(invite.walletAddress);
  return invite.code;
}

function truncateAddress(address: string): string {
  if (address.length <= 12) return address;
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

// ─── Create Invite Dialog ────────────────────────────────────────────

function CreateInviteDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}) {
  const [inviteType, setInviteType] = React.useState<"link" | "email" | "wallet">("link");
  const [userId, setUserId] = React.useState("");
  const [emails, setEmails] = React.useState("");
  const [walletAddresses, setWalletAddresses] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [creating, setCreating] = React.useState(false);
  const [createdLink, setCreatedLink] = React.useState<string | null>(null);

  const resetForm = () => {
    setInviteType("link");
    setUserId("");
    setEmails("");
    setWalletAddresses("");
    setMessage("");
    setCreatedLink(null);
  };

  const handleCreate = async () => {
    if (!userId.trim()) {
      toast.error("User ID is required");
      return;
    }

    setCreating(true);
    try {
      let endpoint: string;
      let body: Record<string, unknown>;

      switch (inviteType) {
        case "link":
          endpoint = "/api/admin/invites/link";
          body = { userId };
          break;
        case "email": {
          endpoint = "/api/admin/invites/email";
          const emailList = emails.split(/[,\n]/).map(e => e.trim()).filter(Boolean);
          if (emailList.length === 0) {
            toast.error("At least one email required");
            setCreating(false);
            return;
          }
          body = { userId, emails: emailList, message: message || undefined };
          break;
        }
        case "wallet": {
          endpoint = "/api/admin/invites/wallet";
          const addressList = walletAddresses.split(/[,\n]/).map(a => a.trim()).filter(Boolean);
          if (addressList.length === 0) {
            toast.error("At least one wallet address required");
            setCreating(false);
            return;
          }
          body = { userId, walletAddresses: addressList, message: message || undefined };
          break;
        }
      }

      const response = await fetch(`${API_ADMIN_BASE_URL}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });

      if (!response.ok) throw new Error("Failed to create invite");

      const result = await response.json();

      if (inviteType === "link" && result.data?.url) {
        setCreatedLink(result.data.url);
        toast.success("Invite link created!");
      } else if (inviteType === "email") {
        toast.success(`${result.data?.sent || 0} email invite(s) sent!`);
        onCreated();
        onOpenChange(false);
        resetForm();
      } else if (inviteType === "wallet") {
        toast.success(`${result.data?.sent || 0} wallet invite(s) sent!`);
        onCreated();
        onOpenChange(false);
        resetForm();
      }
    } catch (error) {
      console.error("Error creating invite:", error);
      toast.error("Failed to create invite");
    } finally {
      setCreating(false);
    }
  };

  const handleCopyLink = () => {
    if (createdLink) {
      navigator.clipboard.writeText(createdLink);
      toast.success("Link copied to clipboard!");
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    if (createdLink) {
      onCreated();
      resetForm();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Create Invite</DialogTitle>
          <DialogDescription>
            Create a new invite on behalf of a user.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="userId">User ID (inviter)</Label>
            <Input
              id="userId"
              placeholder="User ID who sends the invite..."
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>Invite Type</Label>
            <Select value={inviteType} onValueChange={(v) => setInviteType(v as "link" | "email" | "wallet")}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="link">
                  <span className="flex items-center gap-2"><LinkIcon className="h-3.5 w-3.5" /> Link</span>
                </SelectItem>
                <SelectItem value="email">
                  <span className="flex items-center gap-2"><Mail className="h-3.5 w-3.5" /> Email</span>
                </SelectItem>
                <SelectItem value="wallet">
                  <span className="flex items-center gap-2"><Wallet className="h-3.5 w-3.5" /> Wallet</span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {inviteType === "email" && (
            <div className="space-y-2">
              <Label htmlFor="emails">Email Addresses</Label>
              <Textarea
                id="emails"
                placeholder="one@example.com, two@example.com..."
                value={emails}
                onChange={(e) => setEmails(e.target.value)}
                rows={3}
              />
              <p className="text-muted-foreground text-xs">Separate with commas or newlines</p>
            </div>
          )}

          {inviteType === "wallet" && (
            <div className="space-y-2">
              <Label htmlFor="wallets">Wallet Addresses</Label>
              <Textarea
                id="wallets"
                placeholder="0x1234..., 0x5678..."
                value={walletAddresses}
                onChange={(e) => setWalletAddresses(e.target.value)}
                rows={3}
              />
              <p className="text-muted-foreground text-xs">Separate with commas or newlines</p>
            </div>
          )}

          {(inviteType === "email" || inviteType === "wallet") && (
            <div className="space-y-2">
              <Label htmlFor="message">Message (optional)</Label>
              <Textarea
                id="message"
                placeholder="Custom invite message..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={2}
              />
            </div>
          )}

          {createdLink && (
            <div className="rounded-lg border bg-muted/50 p-3 space-y-2">
              <Label>Invite Link Created</Label>
              <div className="flex items-center gap-2">
                <Input value={createdLink} readOnly className="bg-background font-mono text-xs" />
                <Button variant="outline" size="icon" onClick={handleCopyLink}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            {createdLink ? "Done" : "Cancel"}
          </Button>
          {!createdLink && (
            <Button onClick={handleCreate} disabled={creating || !userId.trim()}>
              {creating ? "Creating..." : "Create Invite"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────

function InvitesPage() {
  const navigate = useNavigate();
    const [invites, setInvites] = React.useState<Invite[]>([]);
  const [stats, setStats] = React.useState<InviteStats | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [total, setTotal] = React.useState(0);

  // Filters
  const [search, setSearch] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState<string>("all");
  const [typeFilter, setTypeFilter] = React.useState<string>("all");

  // Combobox open states
  const [statusOpen, setStatusOpen] = React.useState(false);
  const [typeOpen, setTypeOpen] = React.useState(false);

  // Pagination
  const [page, setPage] = React.useState(1);
  const limit = 20;

  // Create dialog
  const [showCreate, setShowCreate] = React.useState(false);

  // Revoking / deleting state
  const [revokingId, setRevokingId] = React.useState<string | null>(null);
  const [deletingId, setDeletingId] = React.useState<string | null>(null);

  // Update URL
  const updateURL = React.useCallback(() => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (statusFilter !== "all") params.set("status", statusFilter);
    if (typeFilter !== "all") params.set("type", typeFilter);
    if (page !== 1) params.set("page", page.toString());

    const queryString = params.toString();
    navigate({ to: queryString ? `?${queryString}` : "/dashboard/invites" });
  }, [search, statusFilter, typeFilter, page,navigate]);

  const fetchInvites = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("limit", limit.toString());
      params.set("offset", ((page - 1) * limit).toString());
      if (search) params.set("search", search);
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (typeFilter !== "all") params.set("type", typeFilter);

      const response = await fetch(`${API_ADMIN_BASE_URL}/api/admin/invites?${params}`, {
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to fetch invites");

      const data = await response.json();
      setInvites(data.data.invites);
      setTotal(data.data.total);
    } catch (error) {
      console.error("Error fetching invites:", error);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, typeFilter, page, limit]);

  const fetchStats = React.useCallback(async () => {
    try {
      const response = await fetch(`${API_ADMIN_BASE_URL}/api/admin/invites/stats`, {
        credentials: "include",
      });
      if (!response.ok) return;
      const data = await response.json();
      setStats(data.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  }, []);

  const handleRevoke = async (id: string) => {
    setRevokingId(id);
    try {
      const response = await fetch(`${API_ADMIN_BASE_URL}/api/admin/invites/${id}/revoke`, {
        method: "PATCH",
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to revoke");
      toast.success("Invite revoked");
      fetchInvites();
      fetchStats();
    } catch (error) {
      console.error("Error revoking invite:", error);
      toast.error("Failed to revoke invite");
    } finally {
      setRevokingId(null);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Permanently delete this invite?")) return;
    setDeletingId(id);
    try {
      const response = await fetch(`${API_ADMIN_BASE_URL}/api/admin/invites/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to delete");
      toast.success("Invite deleted");
      fetchInvites();
      fetchStats();
    } catch (error) {
      console.error("Error deleting invite:", error);
      toast.error("Failed to delete invite");
    } finally {
      setDeletingId(null);
    }
  };

  const handleExpireAll = async () => {
    try {
      const response = await fetch(`${API_ADMIN_BASE_URL}/api/admin/invites/expire`, {
        method: "POST",
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to expire");
      const data = await response.json();
      toast.success(`${data.data.expiredCount} invite(s) expired`);
      fetchInvites();
      fetchStats();
    } catch (error) {
      console.error("Error expiring invites:", error);
      toast.error("Failed to expire invites");
    }
  };

  React.useEffect(() => { updateURL(); }, [updateURL]);
  React.useEffect(() => { fetchInvites(); }, [fetchInvites]);
  React.useEffect(() => { fetchStats(); }, [fetchStats]);
  React.useEffect(() => { document.title = "Invites - Admin"; }, []);
  React.useEffect(() => { setPage(1); }, [search, statusFilter, typeFilter]);

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="bg-background h-full overflow-auto">
      <header>
        <div className="border-border flex h-12 shrink-0 items-center gap-2 border-b px-4 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12 md:h-16 md:px-6">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/dashboard">Dashboard</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Invites</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>

        <div className="border-border flex flex-col border-b px-4 py-4 md:px-6 md:py-6">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold tracking-tight md:text-3xl">Invites</h1>
              <p className="text-muted-foreground text-sm">
                {total.toLocaleString()} invites
                {stats && (
                  <span className="ml-2">
                    · {stats.pending} pending · {stats.accepted} accepted
                  </span>
                )}
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleExpireAll}>
                <Timer className="mr-1.5 h-3.5 w-3.5" />
                Expire Old
              </Button>
              <Button size="sm" onClick={() => setShowCreate(true)}>
                <Plus className="mr-1.5 h-3.5 w-3.5" />
                Create Invite
              </Button>
            </div>
          </div>

          {/* Stats row */}
          {stats && (
            <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-5">
              <div className="flex flex-col">
                <span className="text-muted-foreground text-xs">Total</span>
                <span className="text-lg font-semibold">{stats.total.toLocaleString()}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-muted-foreground text-xs">Pending</span>
                <span className="text-lg font-semibold text-amber-600">{stats.pending.toLocaleString()}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-muted-foreground text-xs">Accepted</span>
                <span className="text-lg font-semibold text-green-600">{stats.accepted.toLocaleString()}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-muted-foreground text-xs">Expired</span>
                <span className="text-lg font-semibold">{stats.expired.toLocaleString()}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-muted-foreground text-xs">Revoked</span>
                <span className="text-lg font-semibold text-red-600">{stats.revoked.toLocaleString()}</span>
              </div>
            </div>
          )}
        </div>
      </header>

      <main className="flex-1 space-y-6 px-4 py-6 md:px-6">
        {/* Filters */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="relative w-full md:w-[300px]">
            <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
            <Input
              placeholder="Search by code, email, wallet..."
              className="pl-8"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="flex gap-3 flex-wrap items-center justify-between w-full">
            <div className="flex gap-3 flex-wrap">
              {/* Status filter */}
              <Popover open={statusOpen} onOpenChange={setStatusOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" className="w-[130px] justify-between">
                    {statusFilter === "all" ? "All Status" : statusFilter}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[150px] p-0">
                  <Command>
                    <CommandList>
                      <CommandGroup>
                        {["all", "PENDING", "ACCEPTED", "EXPIRED", "REVOKED"].map((s) => (
                          <CommandItem
                            key={s}
                            value={s}
                            onSelect={() => { setStatusFilter(s); setStatusOpen(false); }}
                          >
                            <Check className={cn("mr-2 h-4 w-4", statusFilter === s ? "opacity-100" : "opacity-0")} />
                            {s === "all" ? "All Status" : s}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>

              {/* Type filter */}
              <Popover open={typeOpen} onOpenChange={setTypeOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" className="w-[130px] justify-between">
                    {typeFilter === "all" ? "All Types" : typeFilter}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[150px] p-0">
                  <Command>
                    <CommandList>
                      <CommandGroup>
                        {["all", "LINK", "EMAIL", "WALLET"].map((t) => (
                          <CommandItem
                            key={t}
                            value={t}
                            onSelect={() => { setTypeFilter(t); setTypeOpen(false); }}
                          >
                            <Check className={cn("mr-2 h-4 w-4", typeFilter === t ? "opacity-100" : "opacity-0")} />
                            {t === "all" ? "All Types" : t}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            <Button
              variant="ghost"
              size="icon"
              onClick={() => { setSearch(""); setStatusFilter("all"); setTypeFilter("all"); }}
              className="h-9 w-9"
              title="Clear all filters"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Invites Table */}
        <div className="relative overflow-hidden rounded-lg border">
          <div className="overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[60px]">Type</TableHead>
                  <TableHead className="w-[180px]">Target</TableHead>
                  <TableHead className="w-[140px]">Code</TableHead>
                  <TableHead className="w-[90px]">Status</TableHead>
                  <TableHead className="w-[180px]">Invited By</TableHead>
                  <TableHead className="w-[180px]">Accepted By</TableHead>
                  <TableHead className="w-[100px]">Created</TableHead>
                  <TableHead className="w-[100px]">Expires</TableHead>
                  <TableHead className="w-[60px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                      Loading invites...
                    </TableCell>
                  </TableRow>
                ) : invites.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                      No invites found
                    </TableCell>
                  </TableRow>
                ) : (
                  invites.map((invite) => (
                    <TableRow key={invite.id}>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          {getTypeIcon(invite.type)}
                          <span className="text-xs text-muted-foreground">{invite.type}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm font-mono truncate block max-w-[170px]">
                          {getInviteTarget(invite)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-xs font-mono text-muted-foreground">{invite.code}</span>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={getStatusVariant(invite.status)}
                          className={cn("text-xs", getStatusColor(invite.status))}
                        >
                          {invite.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="min-w-0">
                          <p className="text-sm truncate">{invite.invitedBy.name}</p>
                          <p className="text-muted-foreground text-xs truncate">{invite.invitedBy.email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        {invite.acceptedBy ? (
                          <div className="min-w-0">
                            <p className="text-sm truncate">{invite.acceptedBy.name}</p>
                            <p className="text-muted-foreground text-xs truncate">{invite.acceptedBy.email}</p>
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-xs">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="text-muted-foreground text-xs">{formatDate(invite.createdAt)}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-muted-foreground text-xs">{formatDate(invite.expiresAt)}</span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {invite.status === "PENDING" && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              onClick={() => handleRevoke(invite.id)}
                              disabled={revokingId === invite.id}
                              title="Revoke invite"
                            >
                              <Ban className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-destructive hover:text-destructive"
                            onClick={() => handleDelete(invite.id)}
                            disabled={deletingId === invite.id}
                            title="Delete invite"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => setPage(Math.max(1, page - 1))}
                  className={page === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                />
              </PaginationItem>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum: number;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (page <= 3) {
                  pageNum = i + 1;
                } else if (page >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = page - 2 + i;
                }
                return (
                  <PaginationItem key={pageNum}>
                    <PaginationLink
                      onClick={() => setPage(pageNum)}
                      isActive={page === pageNum}
                      className="cursor-pointer"
                    >
                      {pageNum}
                    </PaginationLink>
                  </PaginationItem>
                );
              })}
              <PaginationItem>
                <PaginationNext
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  className={page === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </main>

      {/* Create Invite Dialog */}
      <CreateInviteDialog
        open={showCreate}
        onOpenChange={setShowCreate}
        onCreated={() => { fetchInvites(); fetchStats(); }}
      />
    </div>
  );
}

export const Route = createFileRoute('/dashboard/invites')({
  component: InvitesPage,
})
